import { useCallback, useEffect, useRef, useState } from "react";
import { listActivities, resumeActivity } from "../services/api";
import { logModuleFailure, logModuleSuccess } from "../../telemetry";
import type { RefreshTokenFn } from "../../shared/api/fetchWithRefresh";
import { createPollBackoff } from "../../shared/api/pollBackoff";
import type { Activity, Decision } from "../types";

/** Poll while the pane is visible and the push hub is DOWN. Every change to a
 *  card normally arrives by SignalR (an approval notification when one parks,
 *  an `approval_changed` signal when another approver or a timeout resolves
 *  one), so without the hub this is the only way an open pane learns anything. */
export const FALLBACK_POLL_INTERVAL_MS = 120_000;

/** With the hub UP, a timer tick fetches only if nothing has fetched for this
 *  long. Pushes, reconnects and the pane becoming visible all reset the clock,
 *  so this fires only for what no push covers: a push that never arrived, and
 *  cards that name no approvers, which nobody is pushed for. */
export const CONNECTED_POLL_MIN_GAP_MS = 15 * 60_000;

/** Upper bound of the random delay before a push-triggered refresh. One approval
 *  for a large persona reaches every member's pane at once; the jitter spreads
 *  their fetches over this window instead of landing them in the same second. */
export const PUSH_REFRESH_JITTER_MS = 3_000;

/** A pane becoming visible re-fetches only if the last fetch started at least
 *  this long ago, so switching windows back and forth costs nothing. */
export const VISIBILITY_REFRESH_MIN_GAP_MS = 30_000;

/** How long a just-resolved activity stays suppressed from poll results.
 *  `POST /resume` stamps the decision on the record (`decided`) before it
 *  returns, so the next poll already lists the card as answered; this window
 *  only bridges a poll that was in flight when the user clicked. Bounded, so a
 *  resume the server rejected eventually re-surfaces for another try. */
export const RESOLVED_TTL_MS = 60_000;

/** An answered activity, kept so the list can show it as approved / declined.
 *  Comes from the server once the poller has stamped the decision on the
 *  record; until then (the 202 window) it is the session-local copy. */
export interface DecidedActivity {
  activity: Activity;
  decision: Decision;
  decidedAt: number;
}

export interface UseActivities {
  /** Decisions still awaiting the user's answer. */
  activities: Activity[];
  /** Decisions answered in this session, newest first. */
  decided: DecidedActivity[];
  loading: boolean;
  error: string | null;
  /** The id of the activity currently being submitted, or null. */
  submittingId: string | null;
  /** Fetch now. Coalesced: a call while a fetch is in flight queues one re-run. */
  refresh: () => void;
  /** Fetch after a random 0 to PUSH_REFRESH_JITTER_MS delay; repeat calls while
   *  one is scheduled are absorbed. For triggers that hit many panes at once. */
  refreshSoon: () => void;
  submit: (activity: Activity, decision: Decision) => Promise<void>;
}

/**
 * Jitter fraction in [0, 1), from the Web Crypto RNG: the value only spreads
 * load and carries no security weight, but Sonar flags Math.random
 * (typescript:S2245) — same reasoning as notifications/services/hub.ts.
 */
function randomFraction(): number {
  const [value] = globalThis.crypto.getRandomValues(new Uint32Array(1));
  return (value ?? 0) / 2 ** 32;
}

/** Epoch ms for an activity's creation time; NaN when absent or unparseable. */
function createdAtMs(activity: Activity): number {
  return Date.parse(activity.created_at);
}

/**
 * Newest-first display order (AIVA-7905).
 *
 * `GET /activities` runs `SELECT * FROM c WHERE c.status = @status` with no
 * ORDER BY, so Cosmos returns rows in index order — in practice oldest-first —
 * and the panel rendered that verbatim. Sorting here rather than in the Cosmos
 * query keeps the fix independent of the HITLRecords indexing policy (the
 * container is provisioned by aiva-deploy's Terraform, so a cross-partition
 * ORDER BY on an unindexed `/created_at` would fail the whole request instead
 * of merely mis-ordering it), and this hook already owns the displayed list —
 * it filters just-resolved ids out of every poll result.
 *
 * Rows with no usable `created_at` sort last, and ties break on `id`, so the
 * order is a total one and the list cannot jitter between polls.
 */
export function sortNewestFirst(activities: Activity[]): Activity[] {
  return [...activities].sort((a, b) => {
    const aMs = createdAtMs(a);
    const bMs = createdAtMs(b);
    const aUndated = Number.isNaN(aMs);
    const bUndated = Number.isNaN(bMs);
    if (aUndated !== bUndated) return aUndated ? 1 : -1;
    if (!aUndated && aMs !== bMs) return bMs - aMs;
    return a.id.localeCompare(b.id);
  });
}

/**
 * Loads pending HITL decisions, and submits the user's answer.
 *
 * Refresh is event-driven: the task pane calls `refreshSoon` when a SignalR
 * approval notification or `approval_changed` signal arrives, or the hub
 * reconnects (jittered, since each reaches many panes at once), the hook
 * refreshes when the pane becomes visible (throttled), and a poll, paused while
 * the pane is hidden, covers a hub that is down (every 2 minutes) or a push
 * that never arrived (at most every 15 minutes, see `isPushConnected`). Every
 * path goes through one coalesced fetch, so overlapping triggers never stack
 * requests. The token is read through a ref so none of these restart on a
 * token refresh.
 */
export function useActivities(
  apiToken: string | null,
  refreshTokens?: RefreshTokenFn,
  /** Whether the push hub is connected right now; read at every timer tick.
   *  Absent, the hook assumes no push and keeps the fallback cadence. */
  isPushConnected: () => boolean = () => false,
): UseActivities {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [decided, setDecided] = useState<DecidedActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submittingId, setSubmittingId] = useState<string | null>(null);

  // Read token + refresh through refs so the poll interval doesn't restart on
  // every token refresh.
  const tokenRef = useRef(apiToken);
  tokenRef.current = apiToken;
  const refreshRef = useRef(refreshTokens);
  refreshRef.current = refreshTokens;
  // Same reason: a caller passing an inline function must not restart the timer.
  const isPushConnectedRef = useRef(isPushConnected);
  isPushConnectedRef.current = isPushConnected;

  // ids of activities the user just resolved, mapped to when they were resolved.
  // Filtered out of poll results until the server stops returning them or the
  // TTL lapses — see RESOLVED_TTL_MS.
  const resolvedRef = useRef<Map<string, number>>(new Map());

  // Slows the timer poll after repeated 4xx (a user without access to this
  // endpoint would otherwise log a failure every 15 seconds all day).
  const [pollBackoff] = useState(createPollBackoff);

  const load = useCallback(async () => {
    try {
      setError(null);
      const all = await listActivities(tokenRef.current, refreshRef.current);
      // Pending cards are the awaiting_input rows; anything else the server
      // lists is an answered card — but only if the answer was stamped (older
      // records completed before that existed carry no decision and are skipped).
      const fetched = all.filter((a) => a.status === "awaiting_input");
      const fromServer: DecidedActivity[] = [];
      for (const a of all) {
        const data = a.status !== "awaiting_input" ? a.decision?.data : undefined;
        if (!data?.decision) continue;
        fromServer.push({
          activity: a,
          decision: { decision: data.decision, feedback: data.feedback, inputs: data.inputs },
          decidedAt: Date.parse(a.updated_at) || 0,
        });
      }
      const now = Date.now();
      const resolved = resolvedRef.current;
      // Drop entries past their TTL so a never-claimed resume re-surfaces.
      for (const [id, at] of resolved) {
        if (now - at > RESOLVED_TTL_MS) resolved.delete(id);
      }
      const visible = fetched.filter((a) => !resolved.has(a.id));
      // Once the server stops advertising an id, the resume took — forget it so
      // a future interrupt reusing the id isn't wrongly suppressed.
      const stillPending = new Set(fetched.map((a) => a.id));
      for (const id of resolved.keys()) {
        if (!stillPending.has(id)) resolved.delete(id);
      }
      setActivities(sortNewestFirst(visible));
      // Server-stamped answers win; keep only the session-local ones the
      // server has not caught up with yet (still in the 202 window).
      setDecided((prev) => {
        const known = new Set(fromServer.map((d) => d.activity.id));
        return [...fromServer, ...prev.filter((d) => !known.has(d.activity.id))].sort(
          (x, y) => y.decidedAt - x.decidedAt,
        );
      });
      pollBackoff.recordSuccess();
      logModuleSuccess("approvals", "load-activities");
    } catch (e) {
      pollBackoff.recordFailure(e);
      setError(e instanceof Error ? e.message : "Failed to load approvals");
      logModuleFailure("approvals", e, "load-activities");
    } finally {
      setLoading(false);
    }
  }, [pollBackoff]);

  // One fetch at a time. Mount, a push, a reconnect and the fallback poll can
  // all fire within the same second; while a fetch is running the others mark a
  // single trailing re-run instead of each issuing a request (the same shape as
  // useNotifications' inbox load).
  const inFlightRef = useRef(false);
  const rerunRef = useRef(false);
  const lastFetchStartRef = useRef(0);

  const runCoalesced = useCallback(async () => {
    if (inFlightRef.current) {
      rerunRef.current = true;
      return;
    }
    inFlightRef.current = true;
    // A trigger that arrives mid-fetch earns one more fetch after this one.
    const fetchUntilSettled = async (): Promise<void> => {
      rerunRef.current = false;
      lastFetchStartRef.current = Date.now();
      // load() swallows its own errors.
      await load();
      if (rerunRef.current) await fetchUntilSettled();
    };
    try {
      await fetchUntilSettled();
    } finally {
      inFlightRef.current = false;
    }
  }, [load]);

  const refresh = useCallback(() => {
    runCoalesced().catch(() => {});
  }, [runCoalesced]);

  const jitterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshSoon = useCallback(() => {
    if (jitterTimerRef.current !== null) return;
    jitterTimerRef.current = setTimeout(() => {
      jitterTimerRef.current = null;
      refresh();
    }, Math.round(randomFraction() * PUSH_REFRESH_JITTER_MS));
  }, [refresh]);

  useEffect(() => {
    refresh();
    // Only timer ticks are gated: a refresh a push or the user asks for always
    // runs. Hidden panes skip the tick (becoming visible re-fetches anyway),
    // and with the hub up a tick runs only once nothing else has fetched for
    // CONNECTED_POLL_MIN_GAP_MS.
    const id = setInterval(() => {
      if (document.visibilityState !== "visible" || pollBackoff.shouldSkip()) return;
      if (isPushConnectedRef.current() && Date.now() - lastFetchStartRef.current < CONNECTED_POLL_MIN_GAP_MS) return;
      refresh();
    }, FALLBACK_POLL_INTERVAL_MS);
    // A pane that was hidden (another tab, a minimised host) may have missed
    // pushes; catch up when it is looked at again, unless it fetched recently.
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastFetchStartRef.current < VISIBILITY_REFRESH_MIN_GAP_MS) return;
      refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      if (jitterTimerRef.current !== null) {
        clearTimeout(jitterTimerRef.current);
        jitterTimerRef.current = null;
      }
    };
  }, [refresh, pollBackoff]);

  const submit = useCallback(async (activity: Activity, decision: Decision) => {
    setSubmittingId(activity.id);
    try {
      await resumeActivity(tokenRef.current, activity, decision, refreshRef.current);
      // Suppress this id from subsequent polls until the server stops returning
      // it (the resume only enqueues; the record lingers as awaiting_input).
      resolvedRef.current.set(activity.id, Date.now());
      // Optimistically move the item from pending to decided; the next poll
      // reconciles the pending half.
      setActivities((prev) => prev.filter((a) => a.id !== activity.id));
      setDecided((prev) => [
        { activity, decision, decidedAt: Date.now() },
        ...prev.filter((d) => d.activity.id !== activity.id),
      ]);
      logModuleSuccess("approvals", "submit-decision");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to submit decision");
      logModuleFailure("approvals", e, "submit-decision");
      throw e;
    } finally {
      setSubmittingId(null);
    }
  }, []);

  return {
    activities,
    decided,
    loading,
    error,
    submittingId,
    refresh,
    refreshSoon,
    submit,
  };
}
